import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Form, Input } from 'antd'
import {
  expenseBusinessBatchCreateSchema,
  type ExpenseBusinessBatchCreateResult
} from '@super-mega/server/contracts/expense-business'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'

type BatchFormValues = { names: string }

function parseNames(value: string) {
  return value
    .split(/\r\n|\n|\r/)
    .map((name) => name.trim())
    .filter(Boolean)
}

export function ExpenseBusinessBatchCreateModal({
  onClose,
  onSaved
}: {
  onClose: () => void
  onSaved: () => Promise<unknown>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<BatchFormValues>()
  const [result, setResult] = useState<ExpenseBusinessBatchCreateResult>()
  const batchCreate = useMutation(trpc.expenseBusiness.batchCreate.mutationOptions())

  return (
    <ModalForm<BatchFormValues>
      name="expenseBusiness-batch-create"
      form={form}
      initialValues={{ names: '' }}
      layout="vertical"
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        closable: !batchCreate.isPending,
        keyboard: !batchCreate.isPending,
        onCancel: onClose
      }}
      onFinish={async (values) => {
        try {
          const saved = await batchCreate.mutateAsync({ names: parseNames(values.names) })
          setResult(saved)
          form.setFieldValue('names', saved.failures.map((failure) => failure.name).join('\n'))
          if (saved.successCount > 0) {
            await onSaved()
          }
        } catch (error) {
          void message.error(getErrorMessage(error, '批量添加失败'))
        }
        return false
      }}
      open
      scrollToFirstError
      submitter={{
        resetButtonProps: { disabled: batchCreate.isPending, onClick: onClose },
        searchConfig: { resetText: result ? '关闭' : '取消', submitText: '保存' },
        submitButtonProps: { loading: batchCreate.isPending }
      }}
      title="批量添加支出业务"
      width="40rem"
    >
      {result ? (
        <Alert
          className="mb-4"
          showIcon
          type={result.failureCount ? 'warning' : 'success'}
          title={`成功 ${result.successCount} 个，失败 ${result.failureCount} 个`}
          description={
            result.failureCount ? (
              <div>
                <p>失败的支出业务已保留在下方，可修改后重新保存：</p>
                <ul className="m-0 list-disc pl-5">
                  {result.failures.map((failure, index) => (
                    <li className="break-all" key={`${index}-${failure.name}`}>
                      {failure.name}：{failure.reason}
                    </li>
                  ))}
                </ul>
              </div>
            ) : undefined
          }
        />
      ) : null}
      <Form.Item
        extra="一行一个支出业务名称，最多 100 个；空行自动忽略，使用默认标签颜色。同名支出业务会单独报错，其余正常创建。"
        label="支出业务名称"
        name="names"
        rules={[
          {
            required: true,
            validator: async (_, value: string | undefined) => {
              const parsed = expenseBusinessBatchCreateSchema.shape.names.safeParse(parseNames(value ?? ''))
              if (!parsed.success) {
                throw new Error(parsed.error.issues[0]?.message)
              }
            }
          }
        ]}
      >
        <Input.TextArea disabled={batchCreate.isPending} placeholder={'工资\n佣金\n水费\n电费\n租赁费用'} rows={8} />
      </Form.Item>
    </ModalForm>
  )
}
